import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Loader2, CheckCircle, Link2, BarChart3, Lock, Power } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ToolSettingsPage = () => {
  const [settings, setSettings] = useState({ analyzer_enabled: true, url_enabled: true, scores_enabled: true, locked_section_enabled: true });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/tool-settings`, { headers: getHeaders() });
      setSettings({
        analyzer_enabled: data.analyzer_enabled !== false,
        url_enabled: data.url_enabled !== false,
        scores_enabled: data.scores_enabled !== false,
        locked_section_enabled: data.locked_section_enabled !== false,
      });
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/tool-settings`, settings, { headers: getHeaders() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const Toggle = ({ enabled, onChange, label, description, icon: Icon, highlight }) => (
    <div className={`flex items-center justify-between p-5 rounded-2xl bg-white border shadow-sm ${highlight ? (enabled ? 'border-emerald-200' : 'border-red-200') : 'border-slate-100'}`}>
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${highlight ? (enabled ? 'bg-emerald-50' : 'bg-red-50') : (enabled ? 'bg-blue-50' : 'bg-slate-50')}`}>
          <Icon className={`w-5 h-5 ${highlight ? (enabled ? 'text-emerald-500' : 'text-red-500') : (enabled ? 'text-blue-500' : 'text-slate-300')}`} />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">{label}</p>
          <p className="text-xs text-slate-400 mt-0.5">{description}</p>
        </div>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative w-12 h-7 rounded-full transition-all duration-300 flex-shrink-0 ${highlight ? (enabled ? 'bg-emerald-500' : 'bg-red-400') : (enabled ? 'bg-blue-500' : 'bg-slate-200')}`}
      >
        <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-all duration-300 ${enabled ? 'left-[22px]' : 'left-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div data-testid="tool-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Analyzer Settings</h1>
        <p className="text-sm text-slate-400 mt-1">Control the platform analysis tool behavior</p>
      </div>

      <div className="space-y-4 max-w-2xl">
        {/* Main toggle - Analyzer on/off */}
        <Toggle
          enabled={settings.analyzer_enabled}
          onChange={(v) => setSettings(prev => ({ ...prev, analyzer_enabled: v }))}
          label="Analyzer Tool"
          description={settings.analyzer_enabled ? "The analyzer tool is live on /analyze" : "The analyzer tool is disabled and hidden from visitors"}
          icon={Power}
          highlight
        />

        {/* Divider */}
        <div className="border-t border-slate-100 my-2" />
        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Tool Options</p>

        <Toggle
          enabled={settings.url_enabled}
          onChange={(v) => setSettings(prev => ({ ...prev, url_enabled: v }))}
          label="URL Input"
          description="Allow users to enter their platform URL for deeper analysis"
          icon={Link2}
        />
        <Toggle
          enabled={settings.scores_enabled}
          onChange={(v) => setSettings(prev => ({ ...prev, scores_enabled: v }))}
          label="Score Display"
          description="Show UX, Value Clarity, and Conversion scores in results"
          icon={BarChart3}
        />
        <Toggle
          enabled={settings.locked_section_enabled}
          onChange={(v) => setSettings(prev => ({ ...prev, locked_section_enabled: v }))}
          label="Locked Section (Premium)"
          description="Show locked content section that requires action to unlock"
          icon={Lock}
        />

        <button
          data-testid="save-tool-settings-btn"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm px-6 py-2.5 rounded-xl transition-colors disabled:opacity-60 shadow-lg shadow-blue-500/20 mt-6"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <><CheckCircle className="w-4 h-4" /> Saved</> : <><Save className="w-4 h-4" /> Save Settings</>}
        </button>
      </div>
    </div>
  );
};

export default ToolSettingsPage;
